/**
 * The live console.
 *
 * Server-sent events while the run is going, because that is what the API offers and it
 * survives proxies that mishandle WebSocket upgrades. Once the run is terminal there is
 * nothing left to stream, so a single paged fetch is both cheaper and simpler.
 */

import { useEffect, useState } from "react";

import { endpoints } from "@/api/endpoints";

export interface ConsoleLine {
  ts: number;
  message: string;
}

/**
 * Identity of a console line, for keeping a replay from doubling the output.
 *
 * The server resumes a reconnected stream from the last id it delivered, so this should
 * never fire. It is here because the failure it prevents — every line shown twice — is
 * both silent and the kind of thing that makes people distrust the console entirely.
 */
function keyOf(line: ConsoleLine): string {
  return `${line.ts}\u0000${line.message}`;
}

interface State {
  lines: ConsoleLine[];
  /** True once the stream, or the paged read, has finished. */
  finished: boolean;
  error: string | undefined;
}

const EMPTY: State = { lines: [], finished: false, error: undefined };

export interface ConsoleState extends State {
  streaming: boolean;
  complete: boolean;
}

export function useRunConsole(
  projectSlug: string,
  runId: string,
  isLive: boolean,
): ConsoleState {
  const [state, setState] = useState<State>(EMPTY);

  useEffect(() => {
    // Reset here rather than during render: a second run must not append to the first's
    // output, and the effect re-runs exactly when the run changes.
    setState(EMPTY);

    let cancelled = false;

    if (!isLive) {
      const load = async () => {
        try {
          let token: string | undefined;
          const collected: ConsoleLine[] = [];
          // Bounded: a pathological run should not page forever.
          for (let page = 0; page < 200; page += 1) {
            const result = await endpoints.logs(projectSlug, runId, token);
            collected.push(...result.lines);
            if (!result.next_token || result.next_token === token || result.lines.length === 0) {
              break;
            }
            token = result.next_token;
          }
          if (!cancelled) {
            setState({ lines: collected, finished: true, error: undefined });
          }
        } catch (caught) {
          if (!cancelled) {
            setState((current) => ({ ...current, finished: true, error: (caught as Error).message }));
          }
        }
      };
      void load();
      return () => {
        cancelled = true;
      };
    }

    const source = new EventSource(
      `/api/projects/${encodeURIComponent(projectSlug)}/runs/${runId}/logs/stream`,
      { withCredentials: true },
    );

    const delivered = new Set<string>();

    source.addEventListener("line", (event) => {
      const line = JSON.parse((event as MessageEvent).data) as ConsoleLine;
      const key = keyOf(line);
      if (delivered.has(key)) return;
      delivered.add(key);
      setState((current) => ({ ...current, lines: [...current.lines, line] }));
    });

    source.addEventListener("status", (event) => {
      const payload = JSON.parse((event as MessageEvent).data) as { message?: string };
      if (!payload.message) return;
      setState((current) => ({
        ...current,
        lines: [...current.lines, { ts: Date.now(), message: `— ${payload.message}` }],
      }));
    });

    source.addEventListener("end", () => {
      setState((current) => ({ ...current, finished: true }));
      source.close();
    });

    source.addEventListener("error", (event) => {
      const data = (event as MessageEvent).data;
      if (!data) {
        // A transport error with no payload is the browser reconnecting on its own,
        // which is exactly what SSE is meant to do — so it is not surfaced.
        return;
      }
      let message = "The console stopped unexpectedly.";
      try {
        message = (JSON.parse(data) as { message?: string }).message ?? message;
      } catch {
        // Keep the fallback.
      }
      setState((current) => ({ ...current, error: message, finished: true }));
    });

    return () => {
      cancelled = true;
      source.close();
    };
  }, [projectSlug, runId, isLive]);

  return {
    ...state,
    // Derived rather than stored: one fewer piece of state to keep in step.
    streaming: isLive && !state.finished,
    complete: state.finished,
  };
}
